/**
 * Centrale routeconfig voor sitemap, breadcrumbs, JSON-LD én routing.
 *
 * Bij het toevoegen van een nieuwe pagina:
 * 1. Maak het `.tsx` bestand in `src/pages/` (zonder eigen <Layout> wrapper).
 * 2. Voeg hier één entry toe met `path`, `label`, `seoTitle`, `seoDescription`
 *    en `componentPath` (bestandsnaam zonder extensie).
 *
 * `App.tsx` registreert de route automatisch met SEO-wrapper.
 * Sitemap, breadcrumbs, JSON-LD en canonical worden automatisch bijgewerkt.
 *
 * Dit bestand is bewust framework-agnostisch (geen React-imports) zodat het
 * door de Vite sitemap-plugin in Node kan worden geladen.
 */

export interface SitemapRoute {
  /** URL-pad. */
  path: string;
  /** Hoe vaak Google de pagina opnieuw zou moeten crawlen. */
  changefreq: "daily" | "weekly" | "monthly" | "yearly";
  /** Sitemap-prioriteit (0.0 – 1.0). */
  priority: number;
  /** Breadcrumb-label voor het laatste URL-segment. */
  label?: string;
  /** `<title>` van de pagina (max 60 tekens). Wordt automatisch `"<seoTitle> — Rinqel"`. */
  seoTitle?: string;
  /** Meta description (max 155 tekens). */
  seoDescription?: string;
  /** Optionele extra structured data (Service / FAQPage / Article schema). */
  seoJsonLd?: Record<string, unknown> | Record<string, unknown>[];
  /** Zet op `true` om de pagina uit zoekresultaten te houden. */
  noIndex?: boolean;
  /** Optionele override van de automatische lastmod (YYYY-MM-DD). Zet deze wanneer
   *  er een échte content-wijziging op de pagina is gepubliceerd. Laat leeg om de
   *  laatste git-commitdatum van het paginabestand te gebruiken. */
  lastmod?: string;
  /** Échte publicatiedatum van de pagina (YYYY-MM-DD). Wordt nooit aangepast door
   *  latere refactors of stijlwijzigingen — eerlijk voor SEO. Wordt ook als
   *  `datePublished` in JSON-LD gerenderd. */
  publishedAt?: string;
  /** Bestandsnaam (zonder `.tsx`) onder `src/pages/`. Laat weg voor handmatig
   *  geregistreerde routes (bv. dynamische `/diensten/:slug`). */
  componentPath?: string;
}

import { faqJsonLd, homeFAQ, buildFAQSchema, veiligMailenZorgFAQ } from "./data/faqs";
import {
  buildServiceJsonLd,
  buildHomeOfferCatalog,
  buildCollectionPage,
  catalogByPath,
  serviceCatalog,
  ORG_ID,
  BASE_URL,
} from "./data/services-catalog";

/** Service-schema voor een geregistreerde service-route. Throwt bij typefout. */
const svc = (path: string) => {
  const entry = catalogByPath[path];
  if (!entry) throw new Error(`Geen catalog-entry voor ${path}`);
  return buildServiceJsonLd(entry);
};

const paths = (cat: string) =>
  serviceCatalog.filter((s) => s.category === cat).map((s) => s.path);

// Praktijkvoorbeeld-paden voor de CollectionPage op /praktijkvoorbeelden.
const praktijkPaths = [
  "/praktijkvoorbeelden/byod",
  "/praktijkvoorbeelden/passkeys",
  "/praktijkvoorbeelden/onedrive",
  "/praktijkvoorbeelden/sharepoint",
  "/praktijkvoorbeelden/risicovolle-aanmeldingen",
  "/praktijkvoorbeelden/veilig-mailen-zorg",
];

/** HowTo voor /diensten/werkplek/overstap, gevoed uit MigrationTimeline.tsx. */
const overstapHowTo: Record<string, unknown> = {
  "@context": "https://schema.org",
  "@type": "HowTo",
  "@id": `${BASE_URL}/diensten/werkplek/overstap#howto`,
  name: "Zo verloopt jouw overstap naar Rinqel",
  description:
    "In zes overzichtelijke fasen zetten we je IT-omgeving over naar Rinqel — zonder dataverlies en zonder stilstand.",
  estimatedCost: { "@type": "MonetaryAmount", currency: "EUR", value: 225 },
  totalTime: "P14D",
  step: [
    { "@type": "HowToStep", position: 1, name: "Kennismaking", text: "We brengen samen in kaart hoe jullie nu werken: welke systemen, mailadressen, opslag en apparaten er zijn." },
    { "@type": "HowToStep", position: 2, name: "Inrichting", text: "We richten je Microsoft 365-omgeving volledig in: accounts, beveiliging, e-mail, OneDrive, SharePoint en Teams." },
    { "@type": "HowToStep", position: 3, name: "Datamigratie", text: "E-mail, agenda's, contacten en bestanden verhuizen we zorgvuldig naar de nieuwe omgeving." },
    { "@type": "HowToStep", position: 4, name: "Beveiliging", text: "Tweestapsverificatie, voorwaardelijke toegang en beveiligingsbeleid worden ingesteld volgens de laatste standaarden." },
    { "@type": "HowToStep", position: 5, name: "Apparaten klaarmaken", text: "Laptops, desktops en telefoons worden automatisch ingericht met alle apps en instellingen." },
    { "@type": "HowToStep", position: 6, name: "Go-live", text: "Op de grote dag staan we klaar, begeleiden we het team en zorgen we dat iedereen lekker op gang komt." },
  ],
};

const sitemapRoutes: SitemapRoute[] = [
  {
    path: "/",
    publishedAt: "2026-02-09",
    lastmod: "2026-06-18",
    changefreq: "weekly",
    priority: 1.0,
    seoTitle: "Rinqel — Zakelijke IT zonder gedoe | IT-partner MKB",
    seoDescription:
      "Rinqel is jouw IT-partner voor internet, telefonie, werkplekken en meer. Vast laag maandbedrag, geen verrassingen. Gewoon IT die werkt.",
    // OfferCatalog + FAQPage — de homepage toont een compacte FAQ-sectie met
    // identieke vragen/antwoorden uit /faq, zodat de JSON-LD voldoet aan Google's
    // "FAQ-content must also be visible on the page"-eis.
    seoJsonLd: [buildHomeOfferCatalog(), buildFAQSchema(homeFAQ)],
    componentPath: "Index",
  },
  {
    path: "/faq",
    publishedAt: "2026-06-17",
    lastmod: "2026-06-18",
    changefreq: "monthly",
    priority: 0.8,
    label: "Q&A",
    seoTitle: "Veelgestelde vragen over zakelijke IT | Rinqel",
    seoDescription:
      "Antwoorden op de meestgestelde vragen over Rinqel: kosten, overstappen, support, beveiliging, werkplek, telefonie, internet en mobiel.",
    seoJsonLd: faqJsonLd,
    componentPath: "FAQ",
  },
  {
    path: "/contact",
    publishedAt: "2026-02-09",
    lastmod: "2026-06-18",
    changefreq: "monthly",
    priority: 0.8,
    label: "Contact",
    seoTitle: "Contact — bel, mail of WhatsApp je IT-partner | Rinqel",
    seoDescription:
      "Neem contact op met Rinqel. Bel, mail of WhatsApp ons voor vragen over internet, telefonie, werkplekken of een vrijblijvende offerte.",
    componentPath: "Contact",
  },
  {
    path: "/configurator",
    publishedAt: "2026-02-09",
    lastmod: "2026-06-18",
    changefreq: "monthly",
    priority: 0.8,
    label: "Configurator",
    seoTitle: "Bereken je maandprijs voor zakelijke IT | Rinqel",
    seoDescription:
      "Bereken direct je maandprijs voor zakelijk internet, telefonie en werkplekken. Transparant, eerlijk en zonder verplichtingen.",
    componentPath: "Configurator",
  },
  {
    path: "/diensten",
    publishedAt: "2026-03-05",
    lastmod: "2026-06-18",
    changefreq: "weekly",
    priority: 0.9,
    label: "Diensten",
    seoTitle: "Zakelijke IT-diensten — alles onder één dak | Rinqel",
    seoDescription:
      "Ontdek het complete aanbod van Rinqel: internet, telefonie, werkplekken, netwerken, software en hardware. Alles voor een vast maandbedrag.",
    seoJsonLd: buildCollectionPage(
      "/diensten",
      "Alle IT-diensten",
      "Complete IT-dienstverlening: internet, telefonie, digitale werkplek, netwerken, software en hardware.",
      serviceCatalog.map((s) => s.path),
    ),
    componentPath: "DienstenOverzicht",
  },
  {
    path: "/diensten/internet",
    publishedAt: "2026-02-09",
    lastmod: "2026-06-18",
    changefreq: "monthly",
    priority: 0.8,
    label: "Internet",
    seoTitle: "Zakelijk internet — glasvezel, VDSL & 5G-backup | Rinqel",
    seoDescription:
      "Glasvezel, VDSL, 5G en Starlink voor bedrijven. Met vast IP, managed router en proactief netwerkbeheer. Vanaf €55 per maand.",
    // Afgehandeld door dynamische /diensten/:slug catch-all in App.tsx.
  },
  {
    path: "/diensten/internet/vdsl",
    publishedAt: "2026-02-16",
    lastmod: "2026-06-18",
    changefreq: "monthly",
    priority: 0.7,
    label: "VDSL",
    seoTitle: "Zakelijk VDSL-internet via de telefoonlijn | Rinqel",
    seoDescription:
      "Betrouwbaar zakelijk VDSL internet via de telefoonlijn. Inclusief managed router en netwerkbeheer. Vanaf €55 per maand.",
    seoJsonLd: svc("/diensten/internet/vdsl"),
    componentPath: "VDSLPage",
  },
  {
    path: "/diensten/telefonie",
    publishedAt: "2026-02-09",
    lastmod: "2026-06-18",
    changefreq: "monthly",
    priority: 0.8,
    label: "Telefonie",
    seoTitle: "Zakelijke VoIP-telefonie — cloud telefooncentrale | Rinqel",
    seoDescription:
      "Complete cloud-telefooncentrale met 21 functies inbegrepen, Teams-integratie en CRM-koppelingen. Vanaf €15 per maand voor de centrale.",
    seoJsonLd: [svc("/diensten/telefonie"), buildCollectionPage("/diensten/telefonie", "Zakelijke telefonie", "Cloud-telefonie met centrale, keuzemenu's, nummerportering en transparante tarieven.", paths("Telefonie").filter((p) => p !== "/diensten/telefonie"))],
  },
  {
    path: "/diensten/telefonie/functies",
    publishedAt: "2026-02-20",
    lastmod: "2026-06-18",
    changefreq: "monthly",
    priority: 0.7,
    label: "Functies",
    seoTitle: "21 telefoniefuncties standaard inbegrepen | Rinqel",
    seoDescription:
      "Bel professioneel zonder extra kosten. Keuzemenu, wachtrij, doorschakelen en CRM-koppeling zitten allemaal standaard inbegrepen.",
    seoJsonLd: svc("/diensten/telefonie/functies"),
    componentPath: "TelefonieFeatures",
  },
  {
    path: "/diensten/telefonie/nummers",
    publishedAt: "2026-02-20",
    lastmod: "2026-06-18",
    changefreq: "monthly",
    priority: 0.7,
    label: "Telefoonnummers",
    seoTitle: "Zakelijke telefoonnummers aanvragen | Rinqel",
    seoDescription:
      "Kies je eigen zakelijke telefoonnummers. Lokale nummers, 085-nummers en servicenummers. Eenvoudig te bestellen bij Rinqel.",
    seoJsonLd: svc("/diensten/telefonie/nummers"),
    componentPath: "TelefonieNummers",
  },
  {
    path: "/diensten/telefonie/overstappen",
    publishedAt: "2026-02-20",
    lastmod: "2026-06-18",
    changefreq: "monthly",
    priority: 0.7,
    label: "Overstappen",
    seoTitle: "Overstappen naar VoIP — ISDN vervangen | Rinqel",
    seoDescription:
      "Stap eenvoudig over naar Rinqel zakelijke telefonie. Wij regelen de nummerportering en zorgen voor een naadloze overgang.",
    seoJsonLd: svc("/diensten/telefonie/overstappen"),
    componentPath: "TelefonieOverstappen",
  },
  {
    path: "/diensten/telefonie/gesprekskosten",
    publishedAt: "2026-02-20",
    lastmod: "2026-06-18",
    changefreq: "monthly",
    priority: 0.7,
    label: "Gesprekskosten",
    seoTitle: "Transparante zakelijke gesprekskosten | Rinqel",
    seoDescription:
      "Transparante gesprekskosten bij Rinqel zakelijke telefonie. Bekijk tarieven voor nationaal, mobiel en internationaal bellen.",
    seoJsonLd: svc("/diensten/telefonie/gesprekskosten"),
    componentPath: "TelefonieGesprekskosten",
  },
  {
    path: "/diensten/mobiel",
    publishedAt: "2026-03-02",
    lastmod: "2026-06-18",
    changefreq: "monthly",
    priority: 0.8,
    label: "Zakelijk Mobiel",
    seoTitle: "Zakelijk mobiel — KPN, Odido & Vodafone | Rinqel",
    seoDescription:
      "Zakelijk mobiel bellen en internetten via KPN, Odido of Vodafone. Flexibel, maandelijks opzegbaar en één aanspreekpunt.",
    seoJsonLd: [svc("/diensten/mobiel"), buildCollectionPage("/diensten/mobiel", "Zakelijk mobiel", "Zakelijke mobiele abonnementen via KPN, Odido of Vodafone.", paths("Mobiel").filter((p) => p !== "/diensten/mobiel"))],
    componentPath: "MobielPage",
  },
  {
    path: "/diensten/mobiel/kpn",
    publishedAt: "2026-03-05",
    lastmod: "2026-06-18",
    changefreq: "monthly",
    priority: 0.7,
    label: "KPN",
    seoTitle: "KPN zakelijk mobiel — Sim Only abonnement | Rinqel",
    seoDescription:
      "KPN zakelijk mobiel via Rinqel. Sim Only abonnementen, data-only bundels en buitenlandbundels. Maandelijks opzegbaar.",
    seoJsonLd: svc("/diensten/mobiel/kpn"),
    componentPath: "MobielKPNPage",
  },
  {
    path: "/diensten/mobiel/odido",
    publishedAt: "2026-03-05",
    lastmod: "2026-06-18",
    changefreq: "monthly",
    priority: 0.7,
    label: "Odido",
    seoTitle: "Odido zakelijk mobiel — snel 5G Sim Only | Rinqel",
    seoDescription:
      "Odido zakelijk mobiel via Rinqel. Sim Only en Unlimited abonnementen, data-only bundels. Maandelijks opzegbaar.",
    seoJsonLd: svc("/diensten/mobiel/odido"),
    componentPath: "MobielOdidoPage",
  },
  {
    path: "/diensten/mobiel/vodafone",
    publishedAt: "2026-03-05",
    lastmod: "2026-06-18",
    changefreq: "monthly",
    priority: 0.7,
    label: "Vodafone",
    seoTitle: "Vodafone zakelijk mobiel — Sim Only & 5G | Rinqel",
    seoDescription:
      "Vodafone zakelijk mobiel via Rinqel. Betrouwbare Sim Only abonnementen en data-only bundels. Maandelijks opzegbaar.",
    seoJsonLd: svc("/diensten/mobiel/vodafone"),
    componentPath: "MobielVodafonePage",
  },
  {
    path: "/diensten/netwerken",
    publishedAt: "2026-02-09",
    lastmod: "2026-06-18",
    changefreq: "monthly",
    priority: 0.8,
    label: "Netwerken",
    seoTitle: "Netwerkbeheer voor bedrijven — WiFi & firewall | Rinqel",
    seoDescription:
      "Bedrijfsnetwerken volledig beheerd: router, firewall, switches en WiFi. Inclusief monitoring, updates en troubleshooting.",
    seoJsonLd: svc("/diensten/netwerken"),
  },
  {
    path: "/diensten/werkplek",
    publishedAt: "2026-02-09",
    lastmod: "2026-06-18",
    changefreq: "monthly",
    priority: 0.8,
    label: "Digitale Werkplek",
    seoTitle: "Digitale werkplek met Microsoft 365 | Rinqel",
    seoDescription:
      "Microsoft 365-werkplek beheerd via Intune. Pakketten van Cloud Only tot Premium, voor Windows én macOS. Vanaf €17,50 per gebruiker.",
    seoJsonLd: [svc("/diensten/werkplek"), buildCollectionPage("/diensten/werkplek", "Digitale werkplek", "Managed Microsoft 365-werkplekken in verschillende smaken: Cloud Only, Managed, Kiosk, Mobile Device en meer.", paths("Werkplek").filter((p) => p !== "/diensten/werkplek"))],
  },
  {
    path: "/diensten/werkplek/cloud-only",
    publishedAt: "2026-03-03",
    lastmod: "2026-06-18",
    changefreq: "monthly",
    priority: 0.7,
    label: "Cloud Only Werkplek",
    seoTitle: "Managed Cloud Only werkplek — Microsoft 365 | Rinqel",
    seoDescription:
      "Werk overal veilig in de cloud met e-mail, Teams, OneDrive en SharePoint. Beheerd door Rinqel, vanaf €25 per maand.",
    seoJsonLd: svc("/diensten/werkplek/cloud-only"),
    componentPath: "WerkplekManagedCloudOnly",
  },
  {
    path: "/diensten/werkplek/managed",
    publishedAt: "2026-03-03",
    lastmod: "2026-06-18",
    changefreq: "monthly",
    priority: 0.7,
    label: "Managed Werkplek",
    seoTitle: "Managed werkplek — Microsoft 365 Premium | Rinqel",
    seoDescription:
      "Complete veilige werkplek met Microsoft 365, beveiliging en apparaatbeheer. Proactief beheerd door Rinqel, vanaf €50 per maand.",
    seoJsonLd: svc("/diensten/werkplek/managed"),
    componentPath: "WerkplekManaged",
  },
  {
    path: "/diensten/werkplek/mobile-device",
    publishedAt: "2026-03-03",
    lastmod: "2026-06-18",
    changefreq: "monthly",
    priority: 0.7,
    label: "Mobile Device",
    seoTitle: "Managed mobile device — M365 voor mobiel | Rinqel",
    seoDescription:
      "Beheer zakelijke smartphones en tablets centraal en veilig. Volledig beheerd door Rinqel, zonder gedoe.",
    seoJsonLd: svc("/diensten/werkplek/mobile-device"),
    componentPath: "WerkplekMobileDevice",
  },
  {
    path: "/diensten/werkplek/kiosk",
    publishedAt: "2026-03-03",
    lastmod: "2026-06-18",
    changefreq: "monthly",
    priority: 0.7,
    label: "Kiosk Werkplek",
    seoTitle: "Managed kiosk-werkplek voor gedeelde apparaten | Rinqel",
    seoDescription:
      "Vergrendelde kiosk-werkplekken voor productieomgevingen, recepties en publieke ruimtes. Veilig en eenvoudig beheerd door Rinqel.",
    seoJsonLd: svc("/diensten/werkplek/kiosk"),
    componentPath: "WerkplekKiosk",
  },
  {
    path: "/diensten/werkplek/keeper",
    publishedAt: "2026-03-03",
    lastmod: "2026-06-18",
    changefreq: "monthly",
    priority: 0.7,
    label: "Keeper",
    seoTitle: "Keeper wachtwoordmanager voor bedrijven | Rinqel",
    seoDescription:
      "Centraal wachtwoordbeheer met Keeper Enterprise. SSO via Microsoft 365, veilige kluis per medewerker. Vanaf €6 per gebruiker per maand.",
    seoJsonLd: svc("/diensten/werkplek/keeper"),
    componentPath: "WerkplekKeeper",
  },
  {
    path: "/diensten/werkplek/emailhandtekeningen",
    publishedAt: "2026-03-03",
    lastmod: "2026-06-18",
    changefreq: "monthly",
    priority: 0.7,
    label: "E-mailhandtekeningen",
    seoTitle: "Centrale e-mailhandtekeningen beheren | Rinqel",
    seoDescription:
      "Uniforme, professionele e-mailhandtekeningen voor je hele organisatie. Centraal beheerd, altijd consistent en up-to-date.",
    seoJsonLd: svc("/diensten/werkplek/emailhandtekeningen"),
    componentPath: "WerkplekEmailHandtekeningen",
  },
  {
    path: "/diensten/werkplek/windows",
    publishedAt: "2026-03-03",
    lastmod: "2026-06-18",
    changefreq: "monthly",
    priority: 0.7,
    label: "Windows",
    seoTitle: "Windows-werkplek beheren via Intune | Rinqel",
    seoDescription:
      "Werk zorgeloos op Windows: automatische updates, beveiliging en support door Rinqel. Altijd up-to-date en veilig.",
    seoJsonLd: svc("/diensten/werkplek/windows"),
    componentPath: "WerkplekWindows",
  },
  {
    path: "/diensten/werkplek/macos",
    publishedAt: "2026-03-03",
    lastmod: "2026-06-18",
    changefreq: "monthly",
    priority: 0.7,
    label: "macOS",
    seoTitle: "macOS-werkplek beheren via Intune | Rinqel",
    seoDescription:
      "Beheerde macOS-werkplekken met Microsoft Intune. Apple-apparaten veilig en volledig geïntegreerd in je zakelijke IT.",
    seoJsonLd: svc("/diensten/werkplek/macos"),
    componentPath: "WerkplekMacOS",
  },
  {
    path: "/diensten/werkplek/exchange-online",
    publishedAt: "2026-03-03",
    lastmod: "2026-06-18",
    changefreq: "monthly",
    priority: 0.7,
    label: "Exchange Online",
    seoTitle: "Exchange Online — zakelijke e-mail | Rinqel",
    seoDescription:
      "Zakelijke e-mail met losse en gedeelde mailboxen, 50 GB opslag en back-up. Beheerd door Rinqel, zonder gedoe.",
    seoJsonLd: svc("/diensten/werkplek/exchange-online"),
    componentPath: "WerkplekExchangeOnline",
  },
  {
    path: "/diensten/werkplek/overstap",
    publishedAt: "2026-03-03",
    lastmod: "2026-06-18",
    changefreq: "monthly",
    priority: 0.7,
    label: "Overstap",
    seoTitle: "Overstappen naar Microsoft 365 zonder gedoe | Rinqel",
    seoDescription:
      "Stap zorgeloos over naar Rinqel. Wij regelen de volledige IT-migratie — geen dataverlies, geen stilstand. Eenmalig €225 per werkplek.",
    seoJsonLd: [svc("/diensten/werkplek/overstap"), overstapHowTo],
    componentPath: "WerkplekOverstap",
  },
  {
    path: "/diensten/hardware/inruilen",
    publishedAt: "2026-03-07",
    lastmod: "2026-06-18",
    changefreq: "monthly",
    priority: 0.7,
    label: "Inruilen",
    seoTitle: "Hardware inruilen met gegarandeerde waarde | Rinqel",
    seoDescription:
      "Ruil je oude smartphones, tablets en computers in bij Rinqel. De inruilwaarde verrekenen we bij nieuwe hardware. Duurzaam, veilig en transparant.",
    seoJsonLd: svc("/diensten/hardware/inruilen"),
    componentPath: "HardwareInruilen",
  },
  {
    path: "/praktijkvoorbeelden",
    publishedAt: "2026-03-08",
    lastmod: "2026-06-18",
    changefreq: "weekly",
    priority: 0.8,
    label: "Praktijkvoorbeelden",
    seoTitle: "IT-praktijkvoorbeelden voor het MKB | Rinqel",
    seoDescription:
      "Herkenbare IT-situaties en hoe wij ze oplossen. Van BYOD-beveiliging tot wachtwoordloos inloggen met passkeys.",
    seoJsonLd: {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      "@id": `${BASE_URL}/praktijkvoorbeelden#collection`,
      url: `${BASE_URL}/praktijkvoorbeelden`,
      name: "Praktijkvoorbeelden",
      description: "Herkenbare IT-situaties en hoe Rinqel ze oplost.",
      inLanguage: "nl-NL",
      isPartOf: { "@id": `${BASE_URL}/#website` },
      about: { "@id": ORG_ID },
      mainEntity: {
        "@type": "ItemList",
        itemListElement: praktijkPaths.map((p, i) => ({
          "@type": "ListItem",
          position: i + 1,
          url: `${BASE_URL}${p}`,
          item: { "@id": `${BASE_URL}${p}#article` },
        })),
      },
    },
    componentPath: "PraktijkvoorbeeldenOverzicht",
  },
  {
    path: "/praktijkvoorbeelden/byod",
    publishedAt: "2026-03-08",
    lastmod: "2026-06-18",
    changefreq: "monthly",
    priority: 0.7,
    label: "Bring Your Own Device",
    seoTitle: "BYOD — privételefoons veilig zakelijk inzetten | Rinqel",
    seoDescription:
      "Je medewerkers gebruiken hun eigen telefoon voor werk. Maar is dat veilig? Ontdek hoe je bedrijfsdata beschermt zonder de privételefoon over te nemen.",
    seoJsonLd: {
      "@context": "https://schema.org",
      "@type": "Article",
      "@id": "https://rinqel.nl/praktijkvoorbeelden/byod#article",
      articleSection: "Praktijkvoorbeelden",
      inLanguage: "nl-NL",
      headline: "Bring Your Own Device (BYOD) veilig maken",
      description:
        "Je medewerkers gebruiken hun eigen telefoon voor werk. Maar is dat veilig? Ontdek hoe je bedrijfsdata beschermt zonder de privételefoon over te nemen.",
      image: "https://rinqel.nl/og-image.png",
      author: { "@type": "Organization", name: "Rinqel", "@id": "https://rinqel.nl/#organization" },
      publisher: { "@id": "https://rinqel.nl/#organization" },
      datePublished: "2026-03-08",
      dateModified: "2026-05-19",
      mainEntityOfPage: "https://rinqel.nl/praktijkvoorbeelden/byod",
    },
    componentPath: "PraktijkBYOD",
  },
  {
    path: "/praktijkvoorbeelden/passkeys",
    publishedAt: "2026-03-08",
    lastmod: "2026-06-18",
    changefreq: "monthly",
    priority: 0.7,
    label: "Passkeys",
    seoTitle: "Passkeys — wachtwoordloos & phishingbestendig | Rinqel",
    seoDescription:
      "Wachtwoorden zijn de zwakste schakel in je beveiliging. Ontdek hoe passkeys je bedrijf beschermen tegen phishing — zonder gedoe.",
    seoJsonLd: {
      "@context": "https://schema.org",
      "@type": "Article",
      "@id": "https://rinqel.nl/praktijkvoorbeelden/passkeys#article",
      articleSection: "Praktijkvoorbeelden",
      inLanguage: "nl-NL",
      headline: "Wachtwoordloos inloggen met Passkeys",
      description:
        "Wachtwoorden zijn de zwakste schakel in je beveiliging. Ontdek hoe passkeys je bedrijf beschermen tegen phishing — zonder gedoe.",
      image: "https://rinqel.nl/og-image.png",
      author: { "@type": "Organization", name: "Rinqel", "@id": "https://rinqel.nl/#organization" },
      publisher: { "@id": "https://rinqel.nl/#organization" },
      datePublished: "2026-03-08",
      dateModified: "2026-05-19",
      mainEntityOfPage: "https://rinqel.nl/praktijkvoorbeelden/passkeys",
    },
    componentPath: "PraktijkPasskeys",
  },
  {
    path: "/praktijkvoorbeelden/onedrive",
    publishedAt: "2026-03-08",
    lastmod: "2026-06-18",
    changefreq: "monthly",
    priority: 0.7,
    label: "OneDrive",
    seoTitle: "Lokaal opslaan vs. OneDrive — back-up & beveiliging | Rinqel",
    seoDescription:
      "Waarom het uitmaakt waar je bestanden staan. Ontdek hoe Rinqel met OneDrive zorgt voor back-up, ransomware-bescherming en altijd toegang.",
    seoJsonLd: {
      "@context": "https://schema.org",
      "@type": "Article",
      "@id": "https://rinqel.nl/praktijkvoorbeelden/onedrive#article",
      articleSection: "Praktijkvoorbeelden",
      inLanguage: "nl-NL",
      headline: "Lokaal opslaan vs. OneDrive",
      description:
        "Waarom het uitmaakt waar je bestanden staan. Ontdek hoe Rinqel met OneDrive zorgt voor back-up, ransomware-bescherming en altijd toegang.",
      image: "https://rinqel.nl/og-image.png",
      author: { "@type": "Organization", name: "Rinqel", "@id": "https://rinqel.nl/#organization" },
      publisher: { "@id": "https://rinqel.nl/#organization" },
      datePublished: "2026-03-08",
      dateModified: "2026-05-19",
      mainEntityOfPage: "https://rinqel.nl/praktijkvoorbeelden/onedrive",
    },
    componentPath: "PraktijkOneDrive",
  },
  {
    path: "/praktijkvoorbeelden/sharepoint",
    publishedAt: "2026-03-08",
    lastmod: "2026-06-18",
    changefreq: "monthly",
    priority: 0.7,
    label: "SharePoint",
    seoTitle: "SharePoint vs. NAS of lokale server | Rinqel",
    seoDescription:
      "Waarom organisaties overstappen van NAS en lokale servers naar SharePoint. Teams-integratie, overzichtelijk toegangsbeheer en samenwerken in de cloud.",
    seoJsonLd: {
      "@context": "https://schema.org",
      "@type": "Article",
      "@id": "https://rinqel.nl/praktijkvoorbeelden/sharepoint#article",
      articleSection: "Praktijkvoorbeelden",
      inLanguage: "nl-NL",
      headline: "SharePoint vs. NAS of lokale server",
      description:
        "Waarom organisaties overstappen van NAS en lokale servers naar SharePoint. Teams-integratie, overzichtelijk toegangsbeheer en samenwerken in de cloud.",
      image: "https://rinqel.nl/og-image.png",
      author: { "@type": "Organization", name: "Rinqel", "@id": "https://rinqel.nl/#organization" },
      publisher: { "@id": "https://rinqel.nl/#organization" },
      datePublished: "2026-03-08",
      dateModified: "2026-05-19",
      mainEntityOfPage: "https://rinqel.nl/praktijkvoorbeelden/sharepoint",
    },
    componentPath: "PraktijkSharePoint",
  },
  {
    path: "/praktijkvoorbeelden/risicovolle-aanmeldingen",
    publishedAt: "2026-03-08",
    lastmod: "2026-06-18",
    changefreq: "monthly",
    priority: 0.7,
    label: "Risicovolle aanmeldingen",
    seoTitle: "Risicovolle aanmeldingen herkennen & blokkeren | Rinqel",
    seoDescription:
      "Verdachte inlogpogingen automatisch herkennen en blokkeren. Ontdek hoe je je bedrijf beschermt tegen gelekte wachtwoorden en ongeautoriseerde toegang.",
    seoJsonLd: {
      "@context": "https://schema.org",
      "@type": "Article",
      "@id": "https://rinqel.nl/praktijkvoorbeelden/risicovolle-aanmeldingen#article",
      articleSection: "Praktijkvoorbeelden",
      inLanguage: "nl-NL",
      headline: "Risicovolle aanmeldingen herkennen en blokkeren",
      description:
        "Verdachte inlogpogingen automatisch herkennen en blokkeren. Ontdek hoe je je bedrijf beschermt tegen gelekte wachtwoorden en ongeautoriseerde toegang.",
      image: "https://rinqel.nl/og-image.png",
      author: { "@type": "Organization", name: "Rinqel", "@id": "https://rinqel.nl/#organization" },
      publisher: { "@id": "https://rinqel.nl/#organization" },
      datePublished: "2026-04-01",
      dateModified: "2026-05-19",
      mainEntityOfPage: "https://rinqel.nl/praktijkvoorbeelden/risicovolle-aanmeldingen",
    },
    componentPath: "PraktijkRisicovollAanmeldingen",
  },
  {
    path: "/praktijkvoorbeelden/veilig-mailen-zorg",
    publishedAt: "2026-10-16",
    lastmod: "2026-10-16",
    changefreq: "monthly",
    priority: 0.7,
    label: "Veilig mailen in de zorg",
    seoTitle: "Veilig mailen in de zorg vanuit Outlook | Rinqel",
    seoDescription:
      "Eén e-mailadres voor gewone én veilige mail. Mail vanuit Outlook veilig met patiënten en zorgverleners. Wij regelen alles, zonder gedoe.",
    seoJsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "Article",
        "@id": "https://rinqel.nl/praktijkvoorbeelden/veilig-mailen-zorg#article",
        articleSection: "Praktijkvoorbeelden",
        inLanguage: "nl-NL",
        headline: "Veilig mailen in de zorg, gewoon met je eigen mailadres",
        description:
          "Eén e-mailadres voor gewone én veilige mail. Mail vanuit Outlook veilig met patiënten en zorgverleners. Wij regelen alles, zonder gedoe.",
        image: "https://rinqel.nl/og-image.png",
        author: { "@type": "Organization", name: "Rinqel", "@id": "https://rinqel.nl/#organization" },
        publisher: { "@id": "https://rinqel.nl/#organization" },
        datePublished: "2026-10-16",
        dateModified: "2026-10-16",
        mainEntityOfPage: "https://rinqel.nl/praktijkvoorbeelden/veilig-mailen-zorg",
      },
      buildFAQSchema(veiligMailenZorgFAQ),
    ],
    componentPath: "PraktijkVeiligMailenZorg",
  },
  {
    path: "/klanten",
    publishedAt: "2026-05-19",
    lastmod: "2026-06-18",
    changefreq: "monthly",
    priority: 0.8,
    label: "Klanten",
    seoTitle: "Klantverhalen — bedrijven over hun IT-partner | Rinqel",
    seoDescription:
      "Bedrijven die hun IT aan Rinqel toevertrouwen. Lees verhalen van klanten over zorgeloze ICT, snelle service en persoonlijk contact.",
    seoJsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "ItemList",
        name: "Klanten van Rinqel",
        itemListElement: [
          { "@type": "Organization", name: "Beentjes Hekwerk & Toegangstechniek", url: "https://prefabpoorten.nl/" },
          { "@type": "Organization", name: "Zwembad De Witte Brug", url: "https://www.zwembaddewittebrug.nl/" },
          { "@type": "Organization", name: "Hopman Woonmakelaars", url: "https://hopmanmakelaars.nl/" },
          { "@type": "Organization", name: "Bureau 2030", url: "https://www.bureau2030.nl/" },
          { "@type": "Organization", name: "Lammers Toepoel Installatietechniek", url: "https://lammers-toepoel.nl/" },
          { "@type": "Organization", name: "ViaLuxury", url: "https://vialuxury.com/nl-NL" },
          { "@type": "Organization", name: "Van Wilsem & Cabri", url: "https://www.vwenca.nl/" },
        ],
      },
      {
        "@context": "https://schema.org",
        "@type": "Organization",
        "@id": "https://rinqel.nl/#organization",
        name: "Rinqel",
        url: "https://rinqel.nl",
        aggregateRating: {
          "@type": "AggregateRating",
          ratingValue: "5",
          bestRating: "5",
          worstRating: "1",
          reviewCount: 7,
        },
        review: [
          {
            "@type": "Review",
            author: { "@type": "Person", name: "Barry Beentjes" },
            reviewRating: { "@type": "Rating", ratingValue: "5", bestRating: "5" },
            reviewBody: "Vanaf het eerste gesprek gaf Nick direct vertrouwen. Sinds de samenwerking werkt onze ICT soepel en weten we dat problemen altijd snel en effectief worden opgelost. Het support team is altijd bereikbaar of belt direct terug.",
          },
          {
            "@type": "Review",
            author: { "@type": "Person", name: "Natalie Terlouw" },
            reviewRating: { "@type": "Rating", ratingValue: "5", bestRating: "5" },
            reviewBody: "Nick regelt alles van A tot Z en denkt actief mee. Dankzij hun inzet zijn we soepel overgestapt naar een nieuw systeem voor backoffice en kassa.",
          },
          {
            "@type": "Review",
            author: { "@type": "Person", name: "Michelle Spuls" },
            reviewRating: { "@type": "Rating", ratingValue: "5", bestRating: "5" },
            reviewBody: "Problemen worden altijd snel opgelost en het persoonlijke contact maakt de samenwerking heel prettig. Dankzij Rinqel is onze ICT-omgeving efficiënter en gebruiksvriendelijker geworden.",
          },
          {
            "@type": "Review",
            author: { "@type": "Person", name: "Bas van Lier" },
            reviewRating: { "@type": "Rating", ratingValue: "5", bestRating: "5" },
            reviewBody: "Rinqel heeft ons geholpen met telefonie, laptops en andere ICT-oplossingen. Dankzij hun snelle service en flexibiliteit worden we volledig ontzorgd.",
          },
          {
            "@type": "Review",
            author: { "@type": "Person", name: "Robbie Monteban" },
            reviewRating: { "@type": "Rating", ratingValue: "5", bestRating: "5" },
            reviewBody: "De overstap ging snel en het voorstel was inhoudelijk sterk en scherp geprijsd. In de praktijk werkt alles zoals het moet en worden vragen snel opgepakt.",
          },
          {
            "@type": "Review",
            author: { "@type": "Person", name: "Jan Wegenaar" },
            reviewRating: { "@type": "Rating", ratingValue: "5", bestRating: "5" },
            reviewBody: "Rinqel is voor ons een partner die snel schakelt en goed aanvoelt wat nodig is. Storingen worden direct opgepakt, wat voor ons essentieel is.",
          },
          {
            "@type": "Review",
            author: { "@type": "Person", name: "Harry van Wilsem" },
            reviewRating: { "@type": "Rating", ratingValue: "5", bestRating: "5" },
            reviewBody: "Rinqel heeft onze ICT-omgeving overzichtelijk en toekomstgericht ingericht. Geen wachttijden, maar snel geholpen worden door specialisten.",
          },
        ],
      },
    ],
    componentPath: "Klanten",
  },

  // Categorie-pagina's die afgehandeld worden door de dynamische /diensten/:slug
  // catch-all in App.tsx (ServicePage.tsx). seoTitle/Description worden hier
  // alleen gebruikt voor sitemap.xml en llms.txt — de eigenlijke <title> wordt
  // door ServicePage gezet via een per-slug seoOverrides-map.
  {
    path: "/diensten/software",
    publishedAt: "2026-03-05",
    lastmod: "2026-06-18",
    changefreq: "monthly",
    priority: 0.8,
    label: "Software",
    seoTitle: "Zakelijke software & licenties — Microsoft 365 | Rinqel",
    seoDescription:
      "Microsoft 365, Adobe Creative Cloud en Autodesk-licenties via Rinqel. Activatie, toewijzing en licentiebeheer geregeld.",
  },
  {
    path: "/diensten/hardware",
    publishedAt: "2026-03-05",
    lastmod: "2026-06-18",
    changefreq: "monthly",
    priority: 0.8,
    label: "Hardware",
    seoTitle: "Zakelijke hardware — laptops, telefoons & meer | Rinqel",
    seoDescription:
      "Laptops, desktops, monitoren, telefoons, tablets, printers en netwerkapparatuur — geleverd en geïnstalleerd door Rinqel.",
  },
  {
    path: "/diensten/alles-in-een",
    publishedAt: "2026-03-05",
    lastmod: "2026-06-18",
    changefreq: "monthly",
    priority: 0.9,
    label: "Alles-in-één",
    seoTitle: "Alles-in-één IT — één aanspreekpunt | Rinqel",
    seoDescription:
      "Eén partner voor je complete IT: internet, telefonie, netwerken, werkplekken, software en hardware. Vast laag maandbedrag, maandelijks opzegbaar.",
  },

  // Sitewide zoekpagina — eigen route zodat Google een SearchAction kan tonen.
  // noIndex omdat zoekresultaten zelf niet in de index horen.
  {
    path: "/zoeken",
    publishedAt: "2026-06-17",
    changefreq: "monthly",
    priority: 0.3,
    label: "Zoeken",
    seoTitle: "Zoeken",
    seoDescription:
      "Doorzoek alle pagina's en veelgestelde vragen op rinqel.nl. Vind direct het antwoord of de dienst die je zoekt.",
    componentPath: "Zoeken",
    noIndex: true,
  },

];

export default sitemapRoutes;
